/**
 * The client billing page: /t/:tenantSlug/invoices/clients/:clientId.
 *
 * One client's billing settings without the client record: the rates, who
 * receives the bill and whom the client bills with, and (for a seat that
 * links QuickBooks customers) whether the client is linked. The same two
 * cards the record's Billing tab composes; neither reads the record.
 *
 * Gate: view_invoice, the gate of every read on the page. The client's
 * name comes from the client roster (GET /clients), never from /record,
 * so the billing seat, which holds no client record, can open it. Each
 * card gates its own writes by capability.
 */

import { type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageShell } from '@/components/primitives/PageShell';
import { RatesCard } from '@/components/clientRecord/RatesCard';
import { ArchivedItemsPage } from '@/components/clientRecord/cardSpine';
import { BillingDetailsCard } from '@/components/clientRecord/BillingDetailsCard';
import { useSupportContact } from '@/hooks/useSupportContact';
import { useTenant } from '@/lib/auth';
import { canManageQboCustomerLinks, canViewInvoice } from '@/lib/capabilities';
import { useClientsList } from '@/lib/clientsApi';
import { pageReadText } from '@/lib/errorCopy';
import { practiceTodayIso } from '@/lib/practiceDate';
import { qboApi, type QboLinkProposalsWire } from '@/lib/qboApi';

function linkSentence(data: QboLinkProposalsWire, clientId: string): string {
  if (data.outcome !== 'proposed') return "The QuickBooks link isn't showing right now. Reload the page in a minute.";
  const mine = data.proposals.find((p) => p.client_id === clientId);
  if (mine?.kind === 'already_linked') return `Linked to QuickBooks customer ${mine.qbo_customer_id}.`;
  if (mine?.kind === 'linked_missing') {
    return `This client is linked to a QuickBooks customer that QuickBooks no longer has. Unlink it in Admin, Billing, QuickBooks, then link the right customer.`;
  }
  return 'Link this client to a QuickBooks customer in Admin, QuickBooks.';
}

function QboLinkStatus({ tenantSlug, clientId }: { tenantSlug: string; clientId: string }): ReactNode {
  const supportContact = useSupportContact();
  // The same query the QuickBooks settings read, so a link made there
  // shows here without a second round trip.
  const query = useQuery<QboLinkProposalsWire>({
    queryKey: ['qbo', 'customer-links', tenantSlug],
    queryFn: ({ signal }) => qboApi.linkProposals(tenantSlug, signal),
  });
  return (
    <section className="rounded-card border border-sage-800/[8%] bg-paper p-4 shadow-sh1 sm:p-5">
      <h3 className="font-display text-lg text-sage-800 mb-1">QuickBooks</h3>
      <p className="text-sm text-ink" data-testid="client-billing-qbo-link">
        {query.isLoading
          ? 'Checking the QuickBooks link.'
          : query.isError
            ? pageReadText(query.error, supportContact, 'the QuickBooks link')
            : query.data
              ? linkSentence(query.data, clientId)
              : ''}
      </p>
    </section>
  );
}

export function ClientBilling(): ReactNode {
  const { tenantSlug = '', clientId = '' } = useParams<{ tenantSlug: string; clientId: string }>();
  const { whoami } = useTenant();
  const allowed = canViewInvoice(whoami);
  const roster = useClientsList(tenantSlug, { includeArchived: true, enabled: allowed });
  const client = roster.clients.find((c) => c.id === clientId) ?? null;
  const clientName = client?.display_name ?? '';
  // The practice's day, the record's own today, so the Rates card picks the
  // same current rate on both pages.
  const today = practiceTodayIso();

  return (
    <PageShell
      eyebrow="Invoices"
      title={clientName ? `Billing for ${clientName}` : 'Billing'}
      maxWidth="xl"
      access={{ allowed }}
      async={{
        what: 'the client',
        isLoading: roster.isLoading,
        isError: roster.isError,
        error: roster.error,
        onRetry: roster.refetch,
      }}
      testId="client-billing"
    >
      <div className="flex flex-col gap-4">
        <Link
          to={`/t/${tenantSlug}/invoices`}
          className="text-sm text-sage-700 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
        >
          Back to invoices
        </Link>
        {client === null ? (
          <p className="text-sm text-ink-soft" data-testid="client-billing-not-found">
            This client can't be found. Go back to Invoices and pick the client again.
          </p>
        ) : (
          // LP-40: the page's one archived line (the Rates card's archived
          // rates) sits under the cards.
          <ArchivedItemsPage>
            <div className="grid gap-4 items-start lg:grid-cols-2">
              <RatesCard clientId={clientId} today={today} engagement={client.engagement} />
              <BillingDetailsCard clientId={clientId} clientName={clientName} />
              {canManageQboCustomerLinks(whoami) && <QboLinkStatus tenantSlug={tenantSlug} clientId={clientId} />}
            </div>
          </ArchivedItemsPage>
        )}
      </div>
    </PageShell>
  );
}
